import { EFormType, type SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { effectiveRequestDeadline } from "../qualification/stage";
import type { TimelinePhaseKey } from "./deriveTimelinePhase";
import type { NegotiationStage } from "./negotiationStages";

export interface PhaseDate {
  iso: string;
  // True for dates that are still only the buyer's plan and can move freely as the plan is edited.
  // False covers both what has already been recorded (the award decision) and what is fixed and
  // binding though still ahead (the tilbudsfrist, the frozen karensdato).
  planned: boolean;
  // False where the time of day is arithmetic rather than an hour anyone chose: a plan target
  // computed as an offset, or a date the server pins to the end of its day.
  timed: boolean;
}

const settled = (iso: string | null | undefined): PhaseDate | null =>
  iso ? { iso, planned: false, timed: true } : null;

// StandstillRules.expiryFor lands on LocalTime.MAX of the dispatch date plus the statutory days,
// so the expiry is a whole day in Oslo rather than an instant with a meaningful hour.
const settledEndOfDay = (iso: string | null | undefined): PhaseDate | null =>
  iso ? { iso, planned: false, timed: false } : null;

const target = (iso: string | null | undefined): PhaseDate | null =>
  iso ? { iso, planned: true, timed: false } : null;

// eForms cbc:VersionID of the original notice; change notices carry "02" and up.
const FIRST_NOTICE_VERSION = "01";

// The only record of when a procurement was actually announced. The plan's kunngjøringsdato does
// not qualify: it stays provisional until dispatch re-anchors it, and a private competition never
// re-anchors at all.
//
// The kunngjøring is the original contract notice specifically. A veiledende kunngjøring (PIN_7)
// goes out ahead of it and a tildelingskunngjøring (CAN_29) after, and change notices re-publish
// the same notice under a later version — none of those date the announcement.
const originalNoticePublishedAt = (event: SourcingEventV2Dto): string | null => {
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const published = (publicContext?.publications ?? [])
    .filter(
      (publication) =>
        publication.eFormType === EFormType.Cn16 && publication.noticeVersion === FIRST_NOTICE_VERSION,
    )
    .map((publication) => publication.publishedAt)
    .filter((at): at is string => at != null);
  return published.reduce<string | null>(
    (earliest, at) => (earliest == null || Date.parse(at) < Date.parse(earliest) ? at : earliest),
    null,
  );
};

const planAnchor = (dates: SourcingEventV2Dto["milestoneDates"]): string | null =>
  dates?.plannedTenderDeadline ?? dates?.bidSubmissionDeadline ?? null;

/**
 * The milestone plan chains award, karens and contract signing off the original tilbudsfrist — it
 * knows nothing about negotiation rounds, so on a negotiated procurement they read weeks early.
 * Each is a fixed offset from the anchor, so moving the anchor to the stage the competition
 * actually reached moves them all by the same amount.
 *
 * Approximate for the karensdato, which the BFF rounds to end-of-day Oslo rather than adding a
 * plain offset. That only ever affects a *planned* date: once the award decision exists the binding
 * `standstillExpiresAt` replaces it.
 */
const planShiftMs = (
  dates: SourcingEventV2Dto["milestoneDates"],
  latestStage: NegotiationStage | null,
): number => {
  const anchor = planAnchor(dates);
  if (latestStage == null || latestStage.roundNumber == null || anchor == null) return 0;
  return Date.parse(latestStage.deadline) - Date.parse(anchor);
};

const shifted = (iso: string | null | undefined, shiftMs: number): string | null => {
  if (!iso) return null;
  if (shiftMs === 0) return iso;
  return new Date(Date.parse(iso) + shiftMs).toISOString();
};

/**
 * `latestStage` is where the competition actually stands — the newest sent round, or the original
 * competition when none has been sent. Never the stage selected in the pager: the timeline speaks
 * for the run, not for whichever stage is being browsed.
 */
export const phaseDate = (
  event: SourcingEventV2Dto,
  key: TimelinePhaseKey,
  latestStage: NegotiationStage | null = null,
): PhaseDate | null => {
  const dates = event.milestoneDates;
  const shift = planShiftMs(dates, latestStage);
  switch (key) {
    case "announcement":
      return settled(originalNoticePublishedAt(event)) ?? target(dates?.announcement);
    case "requestDeadline":
      return settled(effectiveRequestDeadline(event));
    // Qualification runs from the request deadline to the invitation — it is a span, not a date.
    case "qualification":
      return null;
    case "questionDeadline":
      return settled(dates?.questionDeadline);
    case "bidDeadline":
      if (latestStage?.roundNumber != null) return settled(latestStage.deadline);
      // Two-stage has no binding tilbudsfrist until the § 23-2 invitation; until then the
      // deadline projected from the forespørselsfrist is all the plan has.
      return settled(dates?.bidSubmissionDeadline) ?? target(dates?.plannedTenderDeadline);
    case "evaluationStart":
      // The stage the run reached carries its own evaluation — recorded once it happened, planned
      // until then. Only without any stage does the bare plan target stand in.
      return latestStage?.evaluation ?? target(dates?.evaluationTarget);
    case "awardDecision":
      return settled(event.award?.awardedAt) ?? target(shifted(dates?.awardTarget, shift));
    case "standstillExpiry":
      return settledEndOfDay(event.award?.standstillExpiresAt) ?? target(shifted(dates?.karensDate, shift));
    case "contractSigning":
      return target(shifted(dates?.contractSigningTarget, shift));
  }
};
